import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import { farmingResultsService } from '@/services/farmingResultsService';
import { ProcessedFarmingMetrics, FarmingResultsProduct } from '@/types/farmingResultsTypes';
import { Skeleton } from '@/components/ui/skeleton';

interface FarmingMetricsDashboardProps {
  product: FarmingResultsProduct | null;
}

export const FarmingMetricsDashboard: React.FC<FarmingMetricsDashboardProps> = ({ product }) => {
  const [metrics, setMetrics] = useState<ProcessedFarmingMetrics | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadMetrics = async () => {
      if (!product) {
        setMetrics(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const farmingMetrics = await farmingResultsService.getProcessedFarmingMetrics(
          product.companyName, 
          product.id
        );
        setMetrics(farmingMetrics);
      } catch (err) {
        console.error('Error loading farming metrics:', err);
        setError('Failed to load farming metrics');
        setMetrics(null);
      } finally {
        setLoading(false);
      }
    };

    loadMetrics();
  }, [product]);

  if (!product) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 dark:text-gray-400">Select a product to view farming metrics</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="grid grid-cols-2 gap-0">
            {[...Array(15)].map((_, i) => (
              <div key={i} className="border-b border-gray-200 dark:border-gray-700 border-r border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-2 h-12">
                  <div className="px-4 py-3 border-r border-gray-200 dark:border-gray-700">
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <div className="px-4 py-3">
                    <Skeleton className="h-4 w-16" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-500 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 dark:text-gray-400">No farming metrics found</p>
      </div>
    );
  }

  // Prepare data in PMT3 format - two columns, compact layout
  const farmingData = [
    { item: 'Days Farming', value: metrics.daysFarming !== null ? metrics.daysFarming.toString() : '-' },
    { item: 'Actual GP', value: metrics.actualGP },
    { item: 'Final GP', value: metrics.finalGP },
    { item: 'Actual GP Margin', value: metrics.actualGPMargin },
    { item: 'Final GP Margin', value: metrics.finalGPMargin },
    { item: 'Default $/CST', value: metrics.defaultCostPerCST },
    { item: '$/CST Override', value: metrics.cstOverride },
    { item: 'Final $/CST', value: metrics.finalCostPerCST },
    { item: 'Limit Farming to Max Bid', value: metrics.limitFarmingToMaxBid },
    { item: 'Total Inventory', value: metrics.totalInventory !== null ? metrics.totalInventory.toLocaleString() : '-' },
    { item: 'Days of Cover', value: metrics.daysOfCover !== null ? Math.round(metrics.daysOfCover).toString() : '-' },
    { item: 'Broad Seeds (Full)', value: metrics.broadSeeds !== null ? metrics.broadSeeds.toLocaleString() : '-' },
    { item: 'CTA Targets', value: metrics.ctaTargets !== null ? metrics.ctaTargets.toLocaleString() : '-' },
    { item: 'Exact Targets', value: metrics.exactTargets !== null ? metrics.exactTargets.toLocaleString() : '-' },
    { item: 'PTA Targets', value: metrics.ptaTargets !== null ? metrics.ptaTargets.toLocaleString() : '-' },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="grid grid-cols-2 gap-0">
        {farmingData.map((metric, index) => (
          <div key={metric.item} className={`
            border-b border-gray-200 dark:border-gray-700
            ${index % 2 === 0 ? 'border-r border-gray-200 dark:border-gray-700' : ''}
            ${index >= farmingData.length - 2 ? 'border-b-0' : ''}
          `}>
            <div className="grid grid-cols-2 h-12">
              <div className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 font-medium border-r border-gray-200 dark:border-gray-700 flex items-center">
                {metric.item}
              </div>
              <div className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 font-semibold flex items-center">
                {metric.value}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};